import { DateTime } from '@ksyos/date-time';
import {
    Button,
    PageHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { assertHasAuthorization } from '@frontend/utils';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import type { IEmbloomQuestionnaire } from '../../interfaces/embloomQuestionnaire';
import { embloomQuestionnairesSchema } from '../../../../common/validation/embloomQuestionnaires';
import { translateKey } from '../../../translate';
import { validate } from '../../../utils/validation';
import { InputCheckbox } from '../../elements/common';

export function EmbloomQuestionnairesList() {
    const abortableFetch = useAbortableFetch();
    const [questionnaires, setQuestionnaires] = useState<IEmbloomQuestionnaire[]>([]);

    assertHasAuthorization(ManagerAuthorization.embloomQuestionnaires);

    useEffect(() => {
        loadQuestionnaires();
    }, []);

    return (
        <PageTemplate>
            <PageHeader
                action={
                    <>
                        <Button
                            onClick={reloadQuestionnaires}
                            variant="outline"
                        >
                            {translateKey('embloomQuestionnaires.refresh')}
                        </Button>
                        <Button onClick={saveQuestionnaires}>{translateKey('common.save')}</Button>
                    </>
                }
                title={translateKey('embloomQuestionnaires.name')}
            />
            <TableContainer>
                <Table spacing="medium">
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translateKey('embloomQuestionnaires.abbreviation')}</TableHeader>
                            <TableHeader>{translateKey('embloomQuestionnaires.description')}</TableHeader>
                            <TableHeader>{translateKey('common.dateAdded')}</TableHeader>
                            <TableHeader>{translateKey('embloomQuestionnaires.isActive')}</TableHeader>
                            <TableHeader>{translateKey('embloomQuestionnaires.isStandard')}</TableHeader>
                            <TableHeader>{translateKey('embloomQuestionnaires.isDecisionSupport')}</TableHeader>
                            <TableHeader>{translateKey('embloomQuestionnaires.autoGrading')}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {questionnaires.map((questionnaire) => (
                            <TableRow key={questionnaire.id}>
                                <TableCell>{questionnaire.label}</TableCell>
                                <TableCell>{questionnaire.description}</TableCell>
                                <TableCell>{DateTime.from(questionnaire.created_at).format('DD-MM-YYYY')}</TableCell>
                                <TableCell>
                                    {questionnaire.isActive ? translateKey('common.yes') : translateKey('common.no')}
                                </TableCell>
                                <TableCell>
                                    <InputCheckbox
                                        checked={questionnaire.isStandard}
                                        onChange={async (value) => handleChange(questionnaire.id, value, 'isStandard')}
                                    />
                                </TableCell>
                                <TableCell>
                                    <InputCheckbox
                                        checked={questionnaire.isDecisionSupport}
                                        onChange={async (value) =>
                                            handleChange(questionnaire.id, value, 'isDecisionSupport')
                                        }
                                    />
                                </TableCell>
                                <TableCell>
                                    <InputCheckbox
                                        checked={questionnaire.isEmbloomGrading}
                                        onChange={async (value) =>
                                            handleChange(questionnaire.id, value, 'isEmbloomGrading')
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </PageTemplate>
    );

    function handleChange(id: string, value: boolean, key: keyof IEmbloomQuestionnaire) {
        const newQuestionnaires = [...questionnaires];
        const itemToUpdate = newQuestionnaires.find((item) => item.id === id);
        if (itemToUpdate) {
            (itemToUpdate as any)[key] = value;
        }
        setQuestionnaires(newQuestionnaires);
    }

    async function loadQuestionnaires() {
        await saveQuestionnaires();

        const url = '/api/embloomQuestionnaires';
        const result = await abortableFetch.fetchJson(url, 'GET');
        setQuestionnaires(result.json.items);
    }

    async function reloadQuestionnaires() {
        const url = '/api/embloomQuestionnaires/reload';
        await abortableFetch.fetchJson(url, 'POST');
        loadQuestionnaires();
    }

    async function saveQuestionnaires() {
        const url = '/api/embloomQuestionnaires';
        const { errors, item } = validate(embloomQuestionnairesSchema, questionnaires);
        if (errors.size > 0) {
            throw new Error('Error in validating embloomQuestionnaires');
        }

        await abortableFetch.fetchJson(url, 'POST', item);
    }
}
